'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { searchEvents } from '../event/client';
import { mapSearchPrediction } from './mappers';
import type { SearchPrediction } from './models';

const SEARCH_PREDICTIONS_QK = 'search-predictions';
const TRENDING_PREDICTIONS_QK = 'search-trending-predictions';

/**
 * Prediction markets for the header search, from the event service rather
 * than the Strapi proxy: the rows show a picture, the volume and the end
 * date, which only the full event carries (13.10). The previous answer stays
 * up while the next one loads, so the list does not blink between keystrokes.
 */
export function useSearchPredictions(query: string, size: number) {
  const locale = useLocale();
  const trimmed = query.trim();
  return useQuery<SearchPrediction[], Error>({
    queryKey: [SEARCH_PREDICTIONS_QK, trimmed, size, locale],
    queryFn: async () =>
      (await searchEvents({ ss: trimmed, l: locale }, { page: 0, size })).map(mapSearchPrediction),
    enabled: trimmed.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

/** The markets trading most over the last day, for the empty search. */
export function useTrendingPredictions(size: number) {
  const locale = useLocale();
  return useQuery<SearchPrediction[], Error>({
    queryKey: [TRENDING_PREDICTIONS_QK, size, locale],
    queryFn: async () =>
      (await searchEvents({ sb: 'VOLUME_24H', sd: 'DESC', l: locale }, { page: 0, size })).map(
        mapSearchPrediction,
      ),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}
